import React, { useEffect, useState } from "react";
import moment from "moment";
import toast from "react-hot-toast";
import { getMcpConnections, disconnectMcpConnection } from "../../services/mcpService";

// Generic spark glyph in Claude's accent colour.
function ClaudeMark() {
  return (
    <svg viewBox="0 0 24 24" fill="#D97757" aria-hidden="true">
      <path d="M12 2.5c.5 0 .9.3 1 .8l.9 5 4.1-3a1 1 0 0 1 1.4 1.4l-3 4.1 5 .9a1 1 0 0 1 0 2l-5 .9 3 4.1a1 1 0 0 1-1.4 1.4l-4.1-3-.9 5a1 1 0 0 1-2 0l-.9-5-4.1 3a1 1 0 0 1-1.4-1.4l3-4.1-5-.9a1 1 0 0 1 0-2l5-.9-3-4.1A1 1 0 0 1 6 5.3l4.1 3 .9-5c.1-.5.5-.8 1-.8z" />
    </svg>
  );
}

// Generic chat-bubble glyph (not a brand logo).
function ChatMark() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="#10A37F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20.5l1.4-4.9A8 8 0 1 1 21 12z" />
      <path d="M8.5 11h7M8.5 14h4.5" />
    </svg>
  );
}

// Both assistants connect to the same /mcp URL; a connection belongs to whichever assistant's
// OAuth client document (client_id) host it came from.
const ASSISTANTS = [
  {
    key: "claude",
    name: "Claude",
    host: "claude.ai",
    icon: <ClaudeMark />,
    tint: "rgba(217,119,87,.16)",
    steps: (
      <>
        <li>In Claude, open <b>Settings → Connectors</b> and choose <b>Add custom connector</b>.</li>
        <li>Paste the URL above and click <b>Add</b>, then <b>Connect</b>.</li>
        <li>Sign in with your dashboard account and click <b>Allow</b>.</li>
      </>
    ),
  },
  {
    key: "chatgpt",
    name: "ChatGPT",
    host: "chatgpt.com",
    icon: <ChatMark />,
    tint: "rgba(16,163,127,.14)",
    steps: (
      <>
        <li>In ChatGPT, open <b>Settings → Apps &amp; Connectors → Advanced</b> and turn on <b>Developer mode</b> (needed for fixing entries).</li>
        <li>Click <b>Create</b>, paste the URL above and choose <b>OAuth</b> authentication.</li>
        <li>Sign in with your dashboard account and click <b>Allow</b>.</li>
      </>
    ),
  },
];

const hostOf = (url) => {
  try {
    return new URL(url).hostname;
  } catch {
    return "";
  }
};

function AssistantRow({ assistant, data, failed, reload }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const connections = (data?.connections || []).filter((c) => hostOf(c.clientId) === assistant.host);
  const inputId = `mcp-url-${assistant.key}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(data.connectorUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Couldn't copy — select the URL and copy it manually.");
    }
  };

  const disconnect = async (c) => {
    if (!window.confirm(`Disconnect ${c.clientName} for ${c.adminName}? It stops working until someone connects again.`)) return;
    if (await disconnectMcpConnection(c.id)) {
      toast.success(`${c.clientName} disconnected`);
      reload();
    } else {
      toast.error("Couldn't disconnect. Please try again.");
    }
  };

  return (
    <div className="integration">
      <div className="left">
        <div className="int-icon" style={{ background: assistant.tint }}>{assistant.icon}</div>
        <div><div className="int-name">{assistant.name}</div><div className="int-desc">Ask {assistant.name} about attendance, or have it fix entries</div></div>
      </div>
      <div className="int-right">
        {connections.length > 0 && <span className="int-badge"><span className="dot" />{connections.length} connected</span>}
        <button type="button" className="int-connect" onClick={() => setOpen((o) => !o)} aria-expanded={open}>
          {open ? "Close" : connections.length ? "Manage" : "Set up"}
        </button>
      </div>

      {open && (
        <div className="mcp-panel">
          {failed && !data && <div className="int-desc">Couldn't load the connector details. Refresh to try again.</div>}
          {data && (
            <>
              <div className="set-field">
                <label htmlFor={inputId}>Connector URL</label>
                <div className="mcp-url-row">
                  <input id={inputId} readOnly className="set-input mono" value={data.connectorUrl} onFocus={(e) => e.target.select()} />
                  <button type="button" className="int-connect" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
                </div>
              </div>
              <ol className="mcp-steps">{assistant.steps}</ol>
              {connections.length > 0 && (
                <ul className="mcp-conns">
                  {connections.map((c) => (
                    <li key={c.id}>
                      <div>
                        <div className="int-name">{c.clientName} · {c.adminName}</div>
                        <div className="int-desc">
                          Connected {moment(c.createdAt).fromNow()} · {c.lastUsedAt ? `last used ${moment(c.lastUsedAt).fromNow()}` : "not used yet"}
                        </div>
                      </div>
                      <button type="button" className="mcp-disconnect" onClick={() => disconnect(c)}>Disconnect</button>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/*
 * Settings → Integrations rows for the AI assistant (MCP) connector: one row each for Claude and
 * ChatGPT, sharing one connector URL. Each shows its setup steps and its own connections, with a
 * Disconnect per connection. Assistants can read attendance and fix/add entries
 * (Owner/Admin/Manager); the backend lives under /mcp and /oauth on the API server.
 */
export default function AiConnectors() {
  const [data, setData] = useState(null); // { connectorUrl, connections }
  const [failed, setFailed] = useState(false);

  const load = async () => {
    const res = await getMcpConnections();
    setFailed(!res);
    if (res) setData(res);
  };

  useEffect(() => {
    load();
  }, []);

  return ASSISTANTS.map((a) => <AssistantRow key={a.key} assistant={a} data={data} failed={failed} reload={load} />);
}
